import React from 'react';
import { Github, ArrowLeft, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const ProjectDetail = ({ project, onBack }) => {
  if (!project) return null;
  
  return (
    <div className="animate-[fadeIn_0.4s_ease-out]">
      {/* BACK BUTTON - Large & Prominent */}
      <button
        onClick={onBack}
        className="group mb-8 flex items-center gap-3 text-[#667eea] hover:text-[#764ba2] transition-all duration-300"
      >
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#667eea]/10 to-[#764ba2]/10 flex items-center justify-center group-hover:bg-gradient-to-r group-hover:from-[#667eea] group-hover:to-[#764ba2] transition-all duration-300">
          <ArrowLeft className="w-6 h-6 group-hover:text-white transition-colors" />
        </div>
        <span className="text-lg font-semibold">Back to Projects</span>
      </button>

      {/* HERO BANNER */}
      <div className="w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden mb-8 shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover"
        />
      </div>

      {/* PROJECT DETAILS CARD */}
      <Card className="border-none shadow-[0_10px_40px_rgba(0,0,0,0.08)]">
        <CardContent className="p-8 md:p-12">
          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent mb-6">
            {project.title}
          </h1>

          {/* Tech Stack Pills */}
          <div className="flex flex-wrap gap-3 mb-8">
            {project.tech.map((tech, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-gradient-to-br from-[#667eea]/10 to-[#764ba2]/10 text-[#667eea] rounded-full text-sm font-bold border-2 border-[#667eea]/20"
              >
                {tech}
              </span>
            ))}
          </div>

          {/* Full Project Narrative */}
          <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Overview</h3>
              <p>
                {project.description}
              </p>
            </div>

            {/* <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">The Challenge</h3>
              <p>
                This project was developed to address specific user needs while maintaining 
                high performance and scalability. The main challenge was creating an intuitive 
                interface that seamlessly integrates with modern technologies.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">The Solution</h3>
              <p>
                We developed a comprehensive application using modern technologies including{' '}
                <span className="font-semibold text-[#667eea]">{project.tech.join(', ')}</span>. 
                The architecture focuses on modularity, maintainability, and exceptional user experience.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Key Features</h3>
              <ul className="space-y-2 ml-6">
                <li className="flex items-start">
                  <span className="text-[#667eea] mr-3 text-xl">▸</span>
                  <span>Modern, responsive user interface built with React</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#667eea] mr-3 text-xl">▸</span>
                  <span>Robust backend architecture ensuring data integrity</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#667eea] mr-3 text-xl">▸</span>
                  <span>Seamless integration with third-party services</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#667eea] mr-3 text-xl">▸</span>
                  <span>Optimized performance and scalability</span>
                </li>
              </ul>
            </div> */}

            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Technologies Used</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {project.tech.map((tech, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200"
                  >
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#667eea] to-[#764ba2]"></div>
                    <span className="font-semibold text-gray-700">{tech}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* GitHub Link */}
          <div className="mt-10 pt-8 border-t border-gray-200 flex flex-wrap gap-4">
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-full font-semibold hover:shadow-[0_10px_30px_rgba(102,126,234,0.4)] transition-all duration-300 hover:translate-y-[-2px]"
            >
              <Github className="w-5 h-5" />
              View Source Code
            </a>
            {project.liveDemo && (
              <a
                href={project.liveDemo}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 border-2 border-[#667eea] text-[#667eea] rounded-full font-semibold hover:bg-gradient-to-br hover:from-[#667eea]/10 hover:to-[#764ba2]/10 transition-all duration-300 hover:translate-y-[-2px]"
              >
                <ExternalLink className="w-5 h-5" />
                Live Demo
              </a>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectDetail;
